'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { createClient as createPlainClient } from '@supabase/supabase-js';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function ForgotPasswordPage() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const appType = process.env.NEXT_PUBLIC_APP_TYPE;
  const loginHref =
    appType === 'admin' ? '/apps/admin/login' : '/apps/vendor/login';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Implicit flow: the emailed link carries its own tokens, so it works in any
    // browser or device (the default PKCE flow only works in the browser that asked).
    const resetClient = createPlainClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      { auth: { flowType: 'implicit', persistSession: false, autoRefreshToken: false, detectSessionInUrl: false } },
    );
    const { error: resetError } = await resetClient.auth.resetPasswordForEmail(
      email,
      {
        redirectTo: `${process.env.NEXT_PUBLIC_APP_URL}/reset-password`,
      },
    );

    if (resetError) {
      setError(resetError.message || 'Failed to send reset email. Please try again.');
      setLoading(false);
      return;
    }

    setSent(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-2xl shadow-warm border border-border p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Icon name="EnvelopeIcon" size={28} className="text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Forgot Password</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Enter your email and we&apos;ll send you a reset link
            </p>
          </div>

          {sent ? (
            <div className="text-center space-y-4">
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <Icon name="CheckCircleIcon" size={24} className="text-green-500 mx-auto mb-2" />
                <p className="text-sm text-foreground font-medium">Reset link sent!</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Check your email at <strong>{email}</strong> for the password reset link.
                </p>
              </div>
              <Link
                href={loginHref}
                className="block w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors text-center"
              >
                Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <div className="text-center">
                <Link
                  href={loginHref}
                  className="text-sm text-primary hover:underline font-medium"
                >
                  ← Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-4">
          © 2026 Zalchos · Secure password reset
        </p>
      </div>
    </div>
  );
}
